// Thin client for the Social Content Engine backend.
//
// During the mock -> real migration only the `assets` resource is wired to the
// backend. Every call is best-effort: if the backend is unreachable the caller
// keeps its local (mock) state, so the app still runs with no server.

// API base resolution:
//  - REACT_APP_API_URL unset            -> dev default (CRA :3000 -> API :8000)
//  - REACT_APP_API_URL="" (empty)       -> same-origin: call "/api" relatively
//    (the production single-container build sets this so the API + SPA share one
//    origin — no CORS, domain-agnostic build).
//  - REACT_APP_API_URL="https://host"   -> explicit absolute base
// IMPORTANT: reference `process.env.X` as exact literal tokens with NO typeof
// guard and NO alias — CRA/webpack statically replaces these at build time and
// folds the result to a constant. A `typeof process` guard survives as a runtime
// check and, since `process` is undefined in the browser bundle, wrongly falls
// through to localhost (that was the prod "Failed to fetch" bug).
// Resolution order:
//   1. explicit REACT_APP_API_URL (incl. "" for same-origin) wins,
//   2. else a PRODUCTION build => same-origin "/api" (the container serves SPA+API),
//   3. else dev => the local API on :8000.
const API_BASE =
  process.env.REACT_APP_API_URL ??
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:8000");

const API_PREFIX = `${API_BASE.replace(/\/$/, "")}/api`;

// --- Auth token (set by AuthProvider; attached to every request) -------------
export const TOKEN_KEY = "adams_ai_token";

export function getToken() {
  try { return (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) || null; }
  catch (_) { return null; }
}
export function setToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch (_) { /* ignore */ }
}

// Called on a 401 so the app can bounce to /login (wired by AuthProvider).
let _onUnauthorized = null;
export function setUnauthorizedHandler(fn) { _onUnauthorized = fn; }

async function request(path, { method = "GET", body, signal } = {}) {
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  // 204/empty bodies are tolerated.
  const text = await res.text();
  if (!res.ok) {
    // An expired/invalid token: drop it and let the app redirect to login.
    if (res.status === 401 && !path.startsWith("/auth/")) {
      setToken(null);
      if (_onUnauthorized) _onUnauthorized();
    }
    // Surface the backend's helpful `detail` message when present. Some
    // generation failures return structured detail with workflow telemetry.
    let detail = "";
    try { detail = JSON.parse(text)?.detail || ""; } catch (_) { /* non-JSON body */ }
    const message = typeof detail === "string"
      ? detail
      : detail?.message || `Request failed (${res.status})`;
    const error = new Error(message);
    error.status = res.status;
    error.statusText = res.statusText;
    if (detail && typeof detail === "object") error.detail = detail;
    throw error;
  }
  return text ? JSON.parse(text) : null;
}

export const authApi = {
  register: (body) => request("/auth/register", { method: "POST", body }),
  login: (body) => request("/auth/login", { method: "POST", body }),
  me: (signal) => request("/auth/me", { signal }),
};

export const assetsApi = {
  list: (signal) => request("/assets", { signal }),
  get: (id) => request(`/assets/${id}`),
  create: (asset) => request("/assets", { method: "POST", body: asset }),
  patch: (id, patch) => request(`/assets/${id}`, { method: "PATCH", body: patch }),
  approve: (id) => request(`/assets/${id}/approve`, { method: "POST" }),
  reject: (id) => request(`/assets/${id}/reject`, { method: "POST" }),
  revise: (id, body) => request(`/assets/${id}/revise`, { method: "POST", body }),
  schedule: (id, scheduleAt) =>
    request(`/assets/${id}/schedule`, { method: "POST", body: { schedule_at: scheduleAt } }),
  publish: (id, authorId) =>
    request(`/assets/${id}/publish`, { method: "POST", body: { author_id: authorId || null } }),
  // Render the real Instagram card image(s) for this asset (single or carousel).
  cards: (id) => request(`/assets/${id}/cards`, { method: "POST" }),
};

export const projectsApi = {
  list: (signal) => request("/projects", { signal }),
  get: (id) => request(`/projects/${id}`),
};

export const calendarApi = {
  list: (signal) => request("/calendar", { signal }),
  create: (item) => request("/calendar", { method: "POST", body: item }),
  patch: (id, patch) => request(`/calendar/${id}`, { method: "PATCH", body: patch }),
  reschedule: (id, scheduledAt) =>
    request(`/calendar/${id}/reschedule`, { method: "POST", body: { scheduled_at: scheduledAt } }),
  publish: (id) => request(`/calendar/${id}/publish`, { method: "POST" }),
  scheduler: (signal) => request("/calendar/scheduler", { signal }),
  runScheduler: () => request("/calendar/run-scheduler", { method: "POST" }),
};

// Fields the backend CalendarItem accepts (extra UI-only keys are stripped).
const CALENDAR_FIELDS = new Set([
  "asset_id", "platform", "title", "scheduled_at", "status", "publish_mode",
]);

export function pickCalendarFields(obj = {}) {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => CALENDAR_FIELDS.has(k)),
  );
}

export const runsApi = {
  list: (signal) => request("/runs", { signal }),
  get: (id) => request(`/runs/${id}`),
  result: (id) => request(`/runs/${id}/result`),
  create: (body) => request("/runs", { method: "POST", body }),
};

export const skillsApi = {
  list: (signal) => request("/skills", { signal }),
  create: (body) => request("/skills", { method: "POST", body }),
  patch: (id, patch) => request(`/skills/${id}`, { method: "PATCH", body: patch }),
  remove: (id) => request(`/skills/${id}`, { method: "DELETE" }),
};

export const inboxApi = {
  list: (signal) => request("/inbox", { signal }),
  archive: (id) => request(`/inbox/${id}/archive`, { method: "POST" }),
  promote: (id) => request(`/inbox/${id}/promote`, { method: "POST" }),
  ingestion: (signal) => request("/inbox/ingestion", { signal }),
  rotateKey: () => request("/inbox/ingest-key/rotate", { method: "POST" }),
  sendTest: () => request("/inbox/ingest-test", { method: "POST" }),
};

export const blogsApi = {
  list: (signal) => request("/blogs", { signal }),
  get: (id) => request(`/blogs/${id}`),
  create: (blog) => request("/blogs", { method: "POST", body: blog }),
  patch: (id, patch) => request(`/blogs/${id}`, { method: "PATCH", body: patch }),
  remove: (id) => request(`/blogs/${id}`, { method: "DELETE" }),
  generate: (id, body) => request(`/blogs/${id}/generate`, { method: "POST", body }),
  publish: (id) => request(`/blogs/${id}/publish`, { method: "POST" }),
  getTarget: (signal) => request("/blogs/target", { signal }),
  saveTarget: (body) => request("/blogs/target", { method: "PUT", body }),
};

// Fields the backend Blog accepts (UI-only keys are stripped before send).
const BLOG_FIELDS = new Set([
  "title", "slug", "status", "cover_image", "seo_title", "seo_meta", "excerpt",
  "tags", "body_html", "word_count", "image_prompt", "author", "schedule_at", "published_at",
]);

export function pickBlogFields(obj = {}) {
  return Object.fromEntries(Object.entries(obj).filter(([k]) => BLOG_FIELDS.has(k)));
}

export const imagesApi = {
  config: (signal) => request("/images/config", { signal }),
  generate: (body) => request("/images/generate", { method: "POST", body }),
  // Persist a user-uploaded image (data: URL) and get back a served URL.
  upload: (body) => request("/images/upload", { method: "POST", body }),
};

export const brandApi = {
  // The backend stores a flexible profile blob; we send/receive the flat object.
  get: (signal) => request("/brand", { signal }),
  save: (profile, industry) =>
    request("/brand", { method: "PUT", body: { profile: profile || {}, industry: industry || null } }),
  packs: (signal) => request("/brand/packs", { signal }),
  applyPack: (packId, brandId) =>
    request("/brand/apply-pack", { method: "POST", body: { pack_id: packId, brand_id: brandId || undefined } }),
  cardThemes: (signal) => request("/brand/card-themes", { signal }),
  cardPreview: (body) => request("/brand/card-preview", { method: "POST", body }),
};

// Multi-brand + voices.
export const brandsApi = {
  list: (signal) => request("/brands", { signal }),
  active: (signal) => request("/brands/active", { signal }),
  create: (body) => request("/brands", { method: "POST", body }),
  patch: (id, body) => request(`/brands/${id}`, { method: "PATCH", body }),
  remove: (id) => request(`/brands/${id}`, { method: "DELETE" }),
  activate: (id) => request(`/brands/${id}/activate`, { method: "POST" }),
  voices: (id, signal) => request(`/brands/${id}/voices`, { signal }),
  createVoice: (id, body) => request(`/brands/${id}/voices`, { method: "POST", body }),
  updateVoice: (vid, body) => request(`/brands/voices/${vid}`, { method: "PATCH", body }),
  removeVoice: (vid) => request(`/brands/voices/${vid}`, { method: "DELETE" }),
  setPrimaryVoice: (vid) => request(`/brands/voices/${vid}`, { method: "PATCH", body: { is_primary: true } }),
  generateIndustry: (body) => request("/brands/generate-industry", { method: "POST", body }),
};

// Build an absolute URL for a backend-served media path (e.g. /images/xyz.jpg).
// Static media is served from the API origin, NOT under /api.
export function mediaUrl(path = "") {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
}

export const sourcesApi = {
  extract: (url) => request("/sources/extract", { method: "POST", body: { url } }),
  extractFile: async (file) => {
    const form = new FormData();
    form.append("file", file);
    // No Content-Type header — the browser sets the multipart boundary.
    const res = await fetch(`${API_PREFIX}/sources/extract-file`, { method: "POST", body: form });
    const text = await res.text();
    if (!res.ok) {
      let detail = "";
      try { detail = JSON.parse(text)?.detail || ""; } catch (_) { /* non-JSON */ }
      throw new Error(detail || `Upload failed (${res.status})`);
    }
    return text ? JSON.parse(text) : null;
  },
};

export const providersApi = {
  list: (signal) => request("/providers", { signal }),
  defaults: (signal) => request("/providers/defaults", { signal }),
  saveDefaults: (body) => request("/providers/defaults", { method: "PUT", body }),
  save: (id, body) => request(`/providers/${id}`, { method: "PUT", body }),
  test: (id) => request(`/providers/${id}/test`, { method: "POST" }),
  models: (id) => request(`/providers/${id}/models`),
  remove: (id) => request(`/providers/${id}`, { method: "DELETE" }),
  setEnabled: (id, enabled) => request(`/providers/${id}/enabled`, { method: "PUT", body: { enabled } }),
};

export const connectionsApi = {
  config: (signal) => request("/connections/config", { signal }),
  list: (signal, brandId) => request(`/connections${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ""}`, { signal }),
  initiate: (platform, brandId) => request(`/connections/${platform}/initiate${brandId ? `?brand_id=${encodeURIComponent(brandId)}` : ""}`, { method: "POST" }),
  refresh: (connId) => request(`/connections/${connId}/refresh`, { method: "POST" }),
  setAuthor: (connId, authorId) => request(`/connections/${connId}/author`, { method: "POST", body: { author_id: authorId } }),
  disconnect: (connId) => request(`/connections/${connId}`, { method: "DELETE" }),
};

// Our integration card ids (int_linkedin) -> backend platform slug (linkedin).
export function integrationPlatform(integrationId = "") {
  return integrationId.replace(/^int_/, "");
}

// Fields the backend Asset model accepts on PATCH. Anything else (UI-only
// metadata) is stripped before sending so the request validates cleanly.
const ASSET_PATCH_FIELDS = new Set([
  "title", "project_id", "platform", "asset_type", "status", "skill_used",
  "hook", "body", "cta", "hashtags", "char_count", "char_limit", "schedule_at",
  "cover_image",
]);

export function pickAssetPatch(patch = {}) {
  return Object.fromEntries(
    Object.entries(patch).filter(([k]) => ASSET_PATCH_FIELDS.has(k)),
  );
}

// Fire-and-forget persistence. Logs on failure but never throws into the UI.
export function persistAssetPatch(id, patch) {
  const clean = pickAssetPatch(patch);
  if (Object.keys(clean).length === 0) return;
  assetsApi.patch(id, clean).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[apiClient] asset patch not persisted:", err.message);
  });
}

// Wrap any best-effort backend call so a failure never breaks the UI.
export function persist(promiseFactory, label = "mutation") {
  try {
    const p = promiseFactory();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(`[apiClient] ${label} not persisted:`, err.message);
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[apiClient] ${label} not persisted:`, err.message);
  }
}

export { API_BASE, API_PREFIX };
